import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Loader2, Check, X, RotateCw } from 'lucide-react';

// Create an image element helper
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.crossOrigin = 'anonymous';
    img.src = url;
  });

async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Bounds for rotated image
  const rotRad = (rotation * Math.PI) / 180;
  const bBoxWidth = Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height);
  const bBoxHeight = Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height);
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // Extract crop
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}

export default function PhotoCropperDialog({ open, imageSrc, onCropped, onCancel, uploading }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixelCrop, setPixelCrop] = useState(null);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setPixelCrop(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    if (!pixelCrop || !imageSrc) return;
    const blob = await getCroppedBlob(imageSrc, pixelCrop, rotation);
    onCropped(blob);
  };

  const reset = () => { setCrop({x:0,y:0}); setZoom(1); setRotation(0); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); reset(); }}>
      <DialogContent className="bg-white rounded-2xl p-0 max-w-md overflow-hidden" data-testid="photo-cropper-dialog">
        <DialogTitle className="sr-only">Crop photo</DialogTitle>
        <div className="p-5 pb-3">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A93] font-bold">Step 1 of 1</p>
          <h2 className="font-display-tight text-xl font-[600] tracking-tight mt-0.5">Crop your photo</h2>
          <p className="text-xs text-[#8A8A93] mt-1">Frame your face for the best first impression.</p>
        </div>
        <div className="relative h-80 bg-[#0F0F10]">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={3 / 4}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              objectFit="contain"
              style={{ containerStyle: { background: '#0F0F10' } }}
            />
          )}
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-bold">Zoom</label>
              <span className="text-xs text-[#0F0F10] font-semibold">{zoom.toFixed(1)}×</span>
            </div>
            <Slider value={[zoom]} onValueChange={(v) => setZoom(v[0])} min={1} max={3} step={0.05} data-testid="crop-zoom" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-bold flex items-center gap-1"><RotateCw size={11}/> Rotate</label>
              <span className="text-xs text-[#0F0F10] font-semibold">{rotation}°</span>
            </div>
            <Slider value={[rotation]} onValueChange={(v) => setRotation(v[0])} min={0} max={360} step={1} data-testid="crop-rotate" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onCancel} disabled={uploading} data-testid="crop-cancel"
              className="flex-1 py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold hover:bg-black/[0.02] inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              <X size={14}/> Cancel
            </button>
            <button onClick={handleCrop} disabled={uploading || !pixelCrop} data-testid="crop-confirm"
              className="btn-shine flex-1 py-3 rounded-full brand-gradient text-white text-sm font-semibold brand-glow-sm hover:scale-[1.01] transition-all disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
              {uploading ? <Loader2 size={14} className="animate-spin"/> : <><Check size={14} strokeWidth={2.5}/> Use photo</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
